import { pickT } from "@repo/shared";
import { FLOW_FIELD_SCOPES, type FlowField, type FlowFieldScope } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiError } from "@/lib/api/_client";
import { duplicateField } from "@/lib/api/flow-editor";
import { T } from "./duplicate-flow-field-dialog.i18n";

// Keyed by the shared scope list, so a new scope fails the type check until it has a label.
const SCOPE_LABEL_KEYS: Record<FlowFieldScope, keyof typeof T.en_GB> = {
  global: "globalScope",
  goal: "goalScope",
  portfolio: "portfolioScope",
};

type DuplicateFlowFieldDialogProps = {
  flowId: string;
  flowLanguage: string;
  /** The field to copy; the dialog is open while it is set. */
  field: FlowField | null;
  onClose: (success: boolean) => void;
};

/**
 * QL-234 — a field's scope is fixed once it exists, so a designer who needs it
 * in another scope duplicates it here, picking the new name and scope at once.
 */
export function DuplicateFlowFieldDialog({
  flowId,
  flowLanguage,
  field,
  onClose,
}: DuplicateFlowFieldDialogProps) {
  const t = pickT(T, flowLanguage);
  const [name, setName] = useState("");
  const [scope, setScope] = useState<FlowFieldScope>("global");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (field) {
      setName(`${field.name} (copy)`);
      setScope(field.scope);
    }
    setError(null);
  }, [field]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!field) return;
    if (!name.trim()) {
      setError(t.fieldNameRequired);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await duplicateField(flowId, field.id, { name: name.trim(), scope });
      onClose(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message || t.failedToDuplicateField
          : t.failedToDuplicateField,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!field} onOpenChange={(open) => !open && !saving && onClose(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.duplicateField}</DialogTitle>
          <DialogDescription>{t.duplicateFieldDescription}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="duplicate-field-name">
                {t.fieldName} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="duplicate-field-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={saving}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="duplicate-field-scope">{t.fieldScope}</Label>
              <Select
                value={scope}
                onValueChange={(value) => setScope(value as FlowFieldScope)}
                disabled={saving}
              >
                <SelectTrigger id="duplicate-field-scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FLOW_FIELD_SCOPES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t[SCOPE_LABEL_KEYS[value]]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <DialogCancel disabled={saving}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={saving}>
              {t.duplicate}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
